// ── Resource manage section — edit + attach + archive, STAFF-only (Wave 8) ──
// CONTRACT (verified against vjal-coaching-api):
//   - EDIT is real: PATCH /resources/:id (manage.resources) updates
//     title/description/resourceType/url/isPublic/cohortIds. The toast fires ONLY
//     after the backend confirms (post-confirm).
//   - ATTACH-TO-PROGRAM/COHORT is part of the same PATCH (cohortIds) and is
//     edited inline via the form modal (scope/global toggle).
//   - ARCHIVE has NO backend route: there is no resource archive/delete endpoint
//     (the resource body has no `status`/`active` field; only upload-level delete
//     exists and it's 503). So Archive renders as a NOT-CONFIGURED honest state
//     and calls NOTHING — it never fabricates an archive. When an archive route
//     lands, this is where it wires in.
//
// STAFF-ONLY: this whole section returns null for any non-staff role (the biting
// test asserts ABSENCE of every manage control for coach / company_admin /
// cohort_lead). A denied write never renders an enabled control.

import { Alert, Button, Module, ModuleBody, ModuleHead, useToast } from "@vjal/ui";
import { Archive, Pencil } from "lucide-react";
import { useState } from "react";

import type {  CoachingResourceDto } from "../../../services/coaching-dtos";
import type { CoachingRole } from "../../../domain/roles";
import { useArtifactWorkflowMutations } from "../../../hooks/useArtifactWorkflowMutations";
import { canManageResources } from "../resources-helpers";
import { describeResourceWriteError } from "../resources-write";
import { ResourceFormModal, type ResourceFormValues , resourceWriteBody } from "../resource-form-modal";

export function ResourceManageSection({ resource, role }: { resource: CoachingResourceDto; role: CoachingRole }) {
  const toast = useToast();
  const [editOpen, setEditOpen] = useState(false);
  const { updateResource } = useArtifactWorkflowMutations();

  // STAFF-ONLY: never render for any non-staff role.
  if (!canManageResources(role)) return null;

  function submitEdit(values: ResourceFormValues) {
    return updateResource.mutateAsync(
      { resourceId: resource.id, body: resourceWriteBody(values) },
      {
        onSuccess: () => {
          toast.success("Resource updated");
          setEditOpen(false);
        }
      }
    );
  }

  const writeError = describeResourceWriteError(updateResource.error);

  return (
    <Module span={4}>
      <ModuleHead title="Manage" subtitle="Editing and archiving follow your role." />
      <ModuleBody className="space-y-3" data-testid="resource-manage">
        {/* Edit — real PATCH. The modal owns the attach-to-program/scope edit. */}
        <Button
          type="button"
          leftIcon={<Pencil size={16} />}
          onClick={() => setEditOpen(true)}
          aria-label="Edit resource"
          data-testid="resource-edit"
        >
          Edit resource
        </Button>

        {/* Archive — NO backend route; honest not-configured (fires NOTHING). */}
        <div data-honest-posture="not-configured" className="space-y-2">
          <Alert variant="warning">
            Archiving isn't available yet — there's no resource archive endpoint. Nothing happens when this is selected.
          </Alert>
          <Button
            type="button"
            variant="secondary"
            leftIcon={<Archive size={16} />}
            disabled
            aria-label="Archive resource"
            data-testid="resource-archive"
          >
            Archive not available yet
          </Button>
        </div>

        {writeError ? <Alert variant={writeError.tone}>{writeError.message}</Alert> : null}

        <ResourceFormModal
          open={editOpen}
          mode="edit"
          resource={resource}
          onClose={() => setEditOpen(false)}
          onSubmit={submitEdit}
          submitting={updateResource.isPending}
          submitError={updateResource.error}
        />
      </ModuleBody>
    </Module>
  );
}
